<template>
  <v-dialog :value="value" max-width="900px" scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="primary white--text">
        {{ title || getLabel('select_collaborateur_title') }}
      </v-card-title>

      <v-card-text class="pt-4">
        <v-text-field
          v-model="query"
          outlined
          dense
          hide-details
          clearable
          prepend-inner-icon="mdi-magnify"
          :placeholder="getLabel('search_collaborateur_placeholder')"
          @input="onQueryInput"
          @click:clear="onQueryInput('')"
        />

        <v-data-table
          class="elevation-1 mt-4 row-pointer"
          :headers="headers"
          :items="collaborators"
          :page="page"
          :items-per-page="itemsPerPage"
          hide-default-footer
          disable-sort
          fixed-header
          :loading="loading"
          :loading-text="getLabel('loading')"
          :no-data-text="getLabel('table_no_data')"
        >
          <template #item="{ item }">
            <tr class="clickable-row" @click="selectCollaborator(item)">
              <td v-for="header in headers" :key="header.value">
                <template v-if="header.value === 'sso_id'">
                  <v-switch readonly :input-value="item.sso_id" class="ma-0" dense hide-details />
                </template>
                <template v-else-if="header.value === 'firstname'">
                  {{ `${item.firstname || ''} ${item.lastname || ''}`.trim() }}
                </template>
                <template v-else>
                  {{ item[header.value] }}
                </template>
              </td>
            </tr>
          </template>
        </v-data-table>

        <div v-if="totalPages > 1" class="d-flex justify-center mt-4">
          <v-pagination :value="page" :length="totalPages" total-visible="7" @input="onPageChange" />
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">
          {{ getLabel('cancel') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<!-- eslint-disable no-underscore-dangle -->
<script setup>
import { ref, defineProps, defineEmits, computed, onMounted } from 'vue';
import getLabel from '@/utils/labels';

const props = defineProps({
  value: { type: Boolean, default: false },
  title: { type: String, default: '' },
  itemsPerPage: { type: Number, default: 8 },
  searchFn: { type: Function, required: true }
});

const emit = defineEmits(['input', 'select']);

const headers = [
  { text: getLabel('digital_account'), value: 'sso_id', align: 'start' },
  { text: getLabel('name'), value: 'firstname', align: 'center' },
  { text: getLabel('phone'), value: 'mobilephone' },
  { text: getLabel('email'), value: 'email' },
  { text: getLabel('registration_number'), value: 'registrationNumber' }
];

const query = ref('');
const page = ref(1);
const total = ref(0);
const collaborators = ref([]);
const loading = ref(false);

const abortController = ref(null);
const debounceTimer = ref(null);

const totalPages = computed(() => (total.value > 0 ? Math.ceil(total.value / props.itemsPerPage) : 0));

const fetchCollaborators = async () => {
  loading.value = true;

  if (abortController.value) {
    abortController.value.abort('Resend Request');
  }
  abortController.value = new AbortController();

  const from = (page.value - 1) * props.itemsPerPage;

  try {
    const { count, items } = await props.searchFn({
      query: query.value || '',
      from: from,
      size: props.itemsPerPage,
      signal: abortController.value.signal
    });
    total.value = count || 0;
    collaborators.value = items || [];
  } catch (err) {
    // aborted requests are expected while typing/paging
    if (err && err.name !== 'AbortError') {
      console.log('ERR search collaborators: ', err);
    }
  } finally {
    loading.value = false;
  }
};

const onQueryInput = () => {
  if (debounceTimer.value) clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => {
    page.value = 1;
    fetchCollaborators();
  }, 500);
};

const selectCollaborator = collaborator => {
  emit('select', collaborator);
};

const onPageChange = newPage => {
  page.value = newPage;
  fetchCollaborators();
};

onMounted(() => {
  fetchCollaborators();
});
</script>

<style lang="scss" scoped>
.row-pointer::v-deep tbody tr:hover {
  cursor: pointer;
}

.clickable-row {
  cursor: pointer;
}
</style>
